import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import toast from "react-hot-toast";
import api from "../../axiosInstance";

// Load User

export const loadUser = createAsyncThunk(
  "user/loadUser",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/user/getuser`);

      return data.user;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
);

// Update User

export const updateUserInformation = createAsyncThunk(
  "user/updateUserInformation",
  async (
    { name, email, phoneNumber, dateOfBirth, country },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await api.put(`/user/update-user-info`, {
        name,
        email,
        phoneNumber,
        dateOfBirth,
        country,
      });

      return data.user;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
);

// enroll to points program
export const enrollLoyaltyProgram = createAsyncThunk(
  "user/enrollLoyaltyProgram",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`/user/enroll-loyalty`, {});

      toast.success(
        data.message || "Successfully enrolled in the loyalty program!",
      );

      return data.user;
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.message ||
        "Unable to enroll in the loyalty program";

      console.error("❌ Loyalty enrollment error:", err);

      toast.error(message);

      return rejectWithValue(message);
    }
  },
);
// unenroll from points program
export const unEnrollLoyaltyProgram = createAsyncThunk(
  "user/unEnrollLoyaltyProgram",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`/user/unenroll-loyalty`, {});

      return data.user;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
);

// Add Address
export const addUserAddress = createAsyncThunk(
  "user/addAddress",
  async (
    { addressType, city, county, country, isDefault },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await api.post(`/user/add-user-address`, {
        addressType,
        city,
        county,
        country,
        isDefault,
      });
      return { successMessage: "Address added successfully!", user: data.user };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
);
// Update Address
export const updateUserAddress = createAsyncThunk(
  "user/updateAddress",
  async (
    { id, city, county, country, addressType, isDefault },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await api.put(`/user/update-user-addresses/${id}`, {
        city,
        county,
        country,
        addressType,
        isDefault,
      });

      return {
        successMessage: "User address updated successfully!",
        user: data.user,
      };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
);

// Delete Address

export const deleteUserAddress = createAsyncThunk(
  "user/deleteAddress",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.delete(`/user/delete-user-address/${id}`);

      return {
        successMessage: "User deleted successfully!",
        user: data.user,
      };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
);

// Get All Users

export const getAllUsers = createAsyncThunk(
  "user/getAllUsers",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/user/admin-all-users`);

      return data.users;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
);

// Update User Role (admin)
export const updateUserRole = createAsyncThunk(
  "user/updateUserRole",
  async ({ id, role }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`/user/admin-update-role/${id}`, {
        role,
      });
      return { id, user: data.user, message: data.message };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
);

// Toggle User Status (admin)
export const toggleUserStatus = createAsyncThunk(
  "user/toggleUserStatus",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`/user/admin-toggle-status/${id}`, {});
      return { id, user: data.user, message: data.message };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
);
const initialState = {
  loading: false,
  loaded: false,
  addressloading: false,
  usersLoading: false,
  isAuthenticated: false,
  user: null,
  users: [],
  error: null,
  successMessage: null,
};

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    clearErrors(state) {
      state.error = null;
    },

    clearMessages(state) {
      state.successMessage = null;
    },
  },

  extraReducers: (builder) => {
    builder

      .addCase(loadUser.pending, (state) => {
        state.loading = true;
      })

      .addCase(loadUser.fulfilled, (state, action) => {
        state.loading = false;
        state.loaded = true;
        state.user = action.payload;
        state.isAuthenticated = true;
      })

      .addCase(loadUser.rejected, (state, action) => {
        state.loading = false;
        state.loaded = true;
        state.error = action.payload;
        state.isAuthenticated = false;
      })

      // Update User
      .addCase(updateUserInformation.pending, (state) => {
        state.loading = true;
      })

      .addCase(updateUserInformation.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
      })

      .addCase(updateUserInformation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(enrollLoyaltyProgram.pending, (state) => {
        state.loading = true;
      })
      .addCase(enrollLoyaltyProgram.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(enrollLoyaltyProgram.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(unEnrollLoyaltyProgram.pending, (state) => {
        state.loading = true;
      })
      .addCase(unEnrollLoyaltyProgram.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(unEnrollLoyaltyProgram.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(addUserAddress.pending, (state) => {
        state.addressloading = true;
        state.error = null;
      })
      .addCase(addUserAddress.fulfilled, (state, action) => {
        state.addressloading = false;
        state.user = action.payload.user;
        state.successMessage = action.payload.successMessage;
      })
      .addCase(addUserAddress.rejected, (state, action) => {
        state.addressloading = false;
        state.error = action.payload;
      })

      // Update Address
      .addCase(updateUserAddress.pending, (state) => {
        state.addressloading = true;
      })

      .addCase(updateUserAddress.fulfilled, (state, action) => {
        state.addressloading = false;
        state.user = action.payload.user;
        state.successMessage = action.payload.successMessage;
      })

      .addCase(updateUserAddress.rejected, (state, action) => {
        state.addressloading = false;
        state.error = action.payload;
      })

      // Delete Address
      .addCase(deleteUserAddress.pending, (state) => {
        state.addressloading = true;
      })

      .addCase(deleteUserAddress.fulfilled, (state, action) => {
        state.addressloading = false;
        state.user = action.payload.user;
        state.successMessage = action.payload.successMessage;
      })

      .addCase(deleteUserAddress.rejected, (state, action) => {
        state.addressloading = false;
        state.error = action.payload;
      })

      // Get All Users
      .addCase(getAllUsers.pending, (state) => {
        state.usersLoading = true;
      })

      .addCase(getAllUsers.fulfilled, (state, action) => {
        state.usersLoading = false;
        state.users = action.payload;
      })

      .addCase(getAllUsers.rejected, (state, action) => {
        state.usersLoading = false;
        state.error = action.payload;
      })
      // Update User Role
      .addCase(updateUserRole.pending, (state) => {
        state.usersLoading = true;
      })
      .addCase(updateUserRole.fulfilled, (state, action) => {
        state.usersLoading = false;
        state.successMessage = action.payload.message;
        const idx = state.users.findIndex((u) => u._id === action.payload.id);
        if (idx !== -1) state.users[idx] = action.payload.user;
      })
      .addCase(updateUserRole.rejected, (state, action) => {
        state.usersLoading = false;
        state.error = action.payload;
      })

      // Toggle User Status
      .addCase(toggleUserStatus.pending, (state) => {
        state.usersLoading = true;
      })
      .addCase(toggleUserStatus.fulfilled, (state, action) => {
        state.usersLoading = false;
        state.successMessage = action.payload.message;
        const idx = state.users.findIndex((u) => u._id === action.payload.id);
        if (idx !== -1) state.users[idx] = action.payload.user;
      })
      .addCase(toggleUserStatus.rejected, (state, action) => {
        state.usersLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearErrors, clearMessages } = userSlice.actions;

export default userSlice.reducer;
